import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { DownloadOutlined } from "@ant-design/icons";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CheckRender from "../../../components/Common/CheckRender";
import Tooltip from "../../../components/Common/Tooltip";
import Colors from "../../../themes/Colors";
import { DATE_FORMATS } from "../../../utils/Constant";
import {
  DetalleDetailRowContainer,
  DetalleDetailRowLabel,
  DetalleDetailRowValue,
  DetalleModalContent,
  DetalleModalTitle,
  DetalleModalHeader,
  DetalleGridContainer,
  DetalleSectionContainer,
  DetalleSectionTitle,
  DetalleSectionParagraph,
  DetalleSubTitle,
  DetalleFlexColumnGap,
  DetalleAdjuntoRow,
  DetalleAdjuntoTextContainer,
  DetalleAdjuntoNombre,
  DetalleAdjuntoMeta,
  DetalleDownloadButton,
  DetalleEmptyText,
  DetalleHistorialSection,
  DetalleHistorialItem,
  DetalleHistorialHeader,
  DetalleHistorialAutor,
  DetalleHistorialFecha,
  DetalleHistorialCuerpo,
} from "../styles/IngresoStyles";

const DetailRow = ({ label, value }) => (
  <DetalleDetailRowContainer>
    <DetalleDetailRowLabel>{label}</DetalleDetailRowLabel>
    <DetalleDetailRowValue>{value ?? "N/A"}</DetalleDetailRowValue>
  </DetalleDetailRowContainer>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const formatTamanio = (tamanio) => {
  if (typeof tamanio !== "number" || isNaN(tamanio)) {
    return "Tamaño desconocido";
  }
  return `${(tamanio / 1024 / 1024).toFixed(2)} MB`;
};

const formatCreado = (creado) => {
  if (!creado) {
    return "Fecha desconocida";
  }
  const date = moment(creado);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "Fecha inválida";
};

const formatMedicosAdicionales = (medicos) => {
  if (Array.isArray(medicos)) {
    return medicos.join(", ");
  }
  return medicos;
};

const DetalleSolicitudHojaQuirurgicaModal = ({
  isOpen,
  onClose,
  detalleSolicitud,
  empresa,
  onDownloadAttachment,
}) => {
  const COLOR_PRINCIPAL = empresa?.COLOR_PRINCIPAL || Colors.primaryPrimera;
  const comentariosHistorial = detalleSolicitud?.comentariosHistorial || [];

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <CheckRender allowed={detalleSolicitud}>
        <DetalleModalHeader>
          <DetalleModalTitle color={COLOR_PRINCIPAL}>
            Detalle de Solicitud: {detalleSolicitud?.numeroSolicitud}
          </DetalleModalTitle>
        </DetalleModalHeader>
        <DetalleModalContent>
          {/* Grid Layout for details */}
          <DetalleGridContainer>
            <DetailRow label="Número de Solicitud" value={detalleSolicitud?.numeroSolicitud} />
            <DetailRow label="Estatus" value={detalleSolicitud?.estado} />
            <DetailRow
              label="Fecha Creación"
              value={
                detalleSolicitud?.fechaCreacion && moment(detalleSolicitud.fechaCreacion).isValid()
                  ? moment(detalleSolicitud.fechaCreacion).format("DD/MM/YYYY HH:mm")
                  : "N/A"
              }
            />
            <DetailRow label="Nombre Afiliado" value={detalleSolicitud?.nombreAfiliado} />
            <DetailRow label="Número de Carnet" value={detalleSolicitud?.numeroCarnet} />
            <DetailRow label="Plan" value={detalleSolicitud?.plan} />
            <DetailRow label="Correo Electrónico" value={detalleSolicitud?.correoElectronico} />
            <DetailRow label="Canal" value={detalleSolicitud?.canal} />
            <DetailRow label="Nombre PSS" value={detalleSolicitud?.nombrePss} />
            <DetailRow label="Tipo PSS" value={detalleSolicitud?.tipoPss} />
            <DetailRow label="Código PSS" value={detalleSolicitud?.codigoPss} />
            <DetailRow label="Zona PSS" value={detalleSolicitud?.zonaPss} />
            <DetailRow label="Número de Ingreso" value={detalleSolicitud?.noIngreso} />
            <DetailRow
              label="Fecha de ingreso"
              value={
                detalleSolicitud?.fechaIngreso && moment(detalleSolicitud.fechaIngreso).isValid()
                  ? moment(detalleSolicitud.fechaIngreso).format(DATE_FORMATS.middledash_ymd)
                  : "N/A"
              }
            />
            <DetailRow
              label="Fecha de egreso"
              value={
                detalleSolicitud?.fechaEgreso && moment(detalleSolicitud.fechaEgreso).isValid()
                  ? moment(detalleSolicitud.fechaEgreso).format(DATE_FORMATS.middledash_ymd)
                  : "N/A"
              }
            />
            <DetailRow label="Motivo de egreso" value={detalleSolicitud?.motivoEgreso} />
            <DetailRow label="Servicio" value={detalleSolicitud?.servicio} />
            <DetailRow label="Médico Tratante" value={detalleSolicitud?.medicoTratante} />
            <DetailRow label="Médicos Adicionales" value={formatMedicosAdicionales(detalleSolicitud?.medicosAdicionales)} />
          </DetalleGridContainer>

          <DetalleSectionContainer>
            <DetalleSectionTitle>Diagnóstico</DetalleSectionTitle>
            <DetalleSectionParagraph>
              {detalleSolicitud?.diagnostico || "N/A"}
            </DetalleSectionParagraph>
          </DetalleSectionContainer>

          <DetalleSectionContainer>
            <DetalleSectionTitle>Comentarios Iniciales</DetalleSectionTitle>
            <DetalleSectionParagraph>
              {detalleSolicitud?.comentarios || "N/A"}
            </DetalleSectionParagraph>
          </DetalleSectionContainer>

          {/* Adjuntos Section */}
          <DetalleSectionContainer>
            <DetalleSubTitle color={COLOR_PRINCIPAL}>
              Documentos Adjuntos
            </DetalleSubTitle>
            <CheckRender allowed={detalleSolicitud?.adjuntos?.length > 0}>
              <DetalleFlexColumnGap>
                {detalleSolicitud?.adjuntos?.map((adjunto) => (
                  <DetalleAdjuntoRow key={adjunto.id}>
                    <DetalleAdjuntoTextContainer>
                      <DetalleAdjuntoNombre>{adjunto.nombreArchivo}</DetalleAdjuntoNombre>
                      <DetalleAdjuntoMeta>
                        Tamaño: {formatTamanio(adjunto.tamanio)} | Creado: {formatCreado(adjunto.creado)}
                      </DetalleAdjuntoMeta>
                    </DetalleAdjuntoTextContainer>
                    <Tooltip text="Descargar">
                      <DetalleDownloadButton
                        type="button"
                        color={COLOR_PRINCIPAL}
                        onClick={() =>
                          onDownloadAttachment(
                            adjunto.id,
                            adjunto.nombreArchivo,
                            detalleSolicitud?.numeroSolicitud
                          )
                        }
                        aria-label={`Descargar archivo ${adjunto.nombreArchivo || ""}`}
                      >
                        <DownloadOutlined />
                      </DetalleDownloadButton>
                    </Tooltip>
                  </DetalleAdjuntoRow>
                ))}
              </DetalleFlexColumnGap>
            </CheckRender>
            <CheckRender allowed={!detalleSolicitud?.adjuntos || detalleSolicitud?.adjuntos?.length === 0}>
              <DetalleEmptyText>No hay archivos adjuntos en esta solicitud.</DetalleEmptyText>
            </CheckRender>
          </DetalleSectionContainer>

          {/* Historial de Comentarios Section */}
          <DetalleHistorialSection>
            <DetalleSubTitle color={COLOR_PRINCIPAL}>
              Histórico de Comentarios
            </DetalleSubTitle>
            {comentariosHistorial.length > 0 ? (
              <DetalleFlexColumnGap>
                {comentariosHistorial.map((comment) => (
                  <DetalleHistorialItem key={comment.id} borderColor={empresa?.COLOR_PRINCIPAL || Colors.huMyHealth}>
                    <DetalleHistorialHeader>
                      <DetalleHistorialAutor>{comment.autor}</DetalleHistorialAutor>
                      <DetalleHistorialFecha>
                        {comment.creado && moment(comment.creado).isValid()
                          ? moment(comment.creado).format("DD/MM/YYYY HH:mm")
                          : "N/A"}
                      </DetalleHistorialFecha>
                    </DetalleHistorialHeader>
                    <DetalleHistorialCuerpo>{comment.cuerpo}</DetalleHistorialCuerpo>
                  </DetalleHistorialItem>
                ))}
              </DetalleFlexColumnGap>
            ) : (
              <DetalleEmptyText>No hay comentarios en el historial.</DetalleEmptyText>
            )}
          </DetalleHistorialSection>
        </DetalleModalContent>
      </CheckRender>
    </CustomModal>
  );
};

DetalleSolicitudHojaQuirurgicaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  detalleSolicitud: PropTypes.object,
  empresa: PropTypes.object,
  onDownloadAttachment: PropTypes.func.isRequired,
};

export default DetalleSolicitudHojaQuirurgicaModal;
